import React from 'react';
import HelpTooltip from '../HelpTooltip';
import OeTip from './OeTip';
import {fmt, fmt0} from './format';
import {channelLabelOf, discountPlan, effectiveChannel, orderedQty} from './discount';
import {deliverableQty} from './availability';

// Rabatt-Ticker über der Tabelle (Original `renderDiscTicker`, Zeile 1004).
// Leitzahl ist bewusst der BESTELLWERT, nicht die lieferbare Menge; weicht
// beides voneinander ab, erscheint die Fußnote. Ein Klick auf einen Chip
// filtert die Tabelle auf die Rabattgruppe.

export default function DiscountTicker({mode, rows, stock, order, locked, onFilterGroup}) {
    // Nur am Auftrag: Rabatte werden dort gesetzt, der Lieferschein kennt
    // weder Preis noch Rabatt (Original `renderDiscTicker`, 1005).
    if (mode !== 'order' || !order || !stock.groups || locked) {
        return null;
    }

    const planOrd = discountPlan(rows, stock, order, orderedQty);
    const delSum = {};
    for (const p of discountPlan(rows, stock, order, (r) => deliverableQty(r, stock, order))) {
        delSum[p.key] = p.sum;
    }

    const chips = [];
    let anyShort = false;

    for (const p of planOrd) {
        // Nur Gruppen mit erreichbarem Ziel.
        if (!p.hasTiers && !p.channel) {
            continue;
        }
        const short = (delSum[p.key] || 0) < p.sum - 0.01;

        if (p.channel) {
            // Palettenpreis: Schwelle in Stück statt in Euro.
            if (p.palletHit) {
                chips.push({
                    key: p.key,
                    cls: 'reached',
                    tip: `Palettenpreis erreicht (${fmt0(p.qty)} Stück).`,
                    body: <>✓ <b>{p.label}</b> Palettenpreis</>,
                });
                continue;
            }
            const gap = Math.max(0, p.channelMinQty - p.qty);
            const near = gap <= Math.max(24, p.channelMinQty * 0.10);
            chips.push({
                key: p.key,
                cls: near ? 'near' : '',
                tip: `Noch ${fmt0(gap)} Stück bis zum Palettenpreis (ab ${fmt0(p.channelMinQty)}).`
                    + (near ? ' Fast erreicht — evtl. Kunde ansprechen.' : ''),
                body: <><b>{p.label}</b> · noch <span className="dt-gap">{fmt0(gap)}</span> Stk bis Palettenpreis</>,
            });
            continue;
        }

        if (p.channelBlocked) {
            chips.push({
                key: p.key,
                cls: 'blocked',
                noClick: true,
                tip: `Kein Staffelrabatt beim Vertriebsweg „${channelLabelOf(effectiveChannel(order), order)}".`,
                body: <><b>{p.label}</b> · Staffel gesperrt</>,
            });
            continue;
        }

        const cur = p.pct !== null ? `${p.pct} %` : '0 %';
        if (!p.next) {
            chips.push({
                key: p.key,
                cls: 'reached',
                tip: `Höchste Staffel erreicht (${cur}).`,
                body: <>✓ <b>{p.label}</b> {cur}</>,
            });
            continue;
        }

        const gap = Math.max(0, p.next[0] - p.sum);
        const near = gap <= Math.max(50, p.next[0] * 0.10);
        if (short) {
            anyShort = true;
        }
        chips.push({
            key: p.key,
            cls: near ? 'near' : '',
            tip: `Warenwert ${fmt(p.sum)} € (Bestellung) · noch ${fmt0(Math.ceil(gap))} € bis ${p.next[1]} %.`
                + (near ? ' Fast erreicht — evtl. Kunde ansprechen.' : '')
                + (short ? ` Achtung: davon nur ${fmt(delSum[p.key] || 0)} € aktuell lieferbar.` : ''),
            body: <>
                <b>{p.label}</b> <span className="dt-cur">{cur}</span> · noch{' '}
                <span className="dt-gap">{fmt0(Math.ceil(gap))} €</span> bis {p.next[1]} %
            </>,
        });
    }

    if (!chips.length) {
        return null;
    }

    return (
        <div className="disc-ticker" style={{display: 'flex'}}>
            <span className="dt-lbl">
                Rabatt-Staffeln:
                <HelpTooltip id="oe-ticker-help" placement="top" size={0.7}>
                    Je Rabattgruppe der erreichte Satz und der Abstand zur nächsten Stufe, gerechnet auf den
                    Bestellwert. Ein Klick auf einen Chip filtert die Tabelle auf die Artikel dieser Gruppe.
                </HelpTooltip>
            </span>
            {chips.map((c, i) => (
                <OeTip key={`${c.key}-${i}`} tip={c.tip}>
                    <span
                        className={`dt-chip ${c.cls}`}
                        role={c.noClick ? undefined : 'button'}
                        tabIndex={c.noClick ? undefined : 0}
                        onClick={c.noClick ? undefined : () => onFilterGroup(c.key)}
                        onKeyDown={c.noClick ? undefined : (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onFilterGroup(c.key);
                            }
                        }}
                    >
                        {c.body}
                    </span>
                </OeTip>
            ))}
            {anyShort ? (
                <div className="dt-foot">
                    Werte = <b>Bestellwert</b> — eine Staffel greift nur, soweit die Menge auch lieferbar ist.
                </div>
            ) : null}
        </div>
    );
}
